<template>
  <div class="advance-reporting-popup">
    <ErrorHandler
      v-if="state.error != null"
      :error="state.error"
      @done="state.error = null"
    />
    <template v-if="state.loading">
      <div class="d-flex justify-content-center">
        <CSpinner color="primary" />
      </div>
    </template>
    <template v-else>
      <div class="popup-content">
        <div class="section">
          <div class="field-group">
            <label class="field-label">Format</label>
            <div class="advance-reporting-radio-group">
              <div
                v-for="format in REPORT_FORMATS"
                :key="format.id"
                class="radio-option"
              >
                <div class="radio-container">
                  <input
                    type="radio"
                    :id="format.id"
                    :value="format.id"
                    v-model="state.selectedFormat"
                    class="radio-input"
                  />
                  <div
                    class="radio-visual"
                    :class="{ checked: state.selectedFormat === format.id }"
                    @click="state.selectedFormat = format.id"
                  >
                    <div
                      class="radio-dot"
                      v-if="state.selectedFormat === format.id"
                    ></div>
                  </div>
                </div>
                <label :for="format.id" class="radio-label">{{
                  format.name
                }}</label>
              </div>
            </div>
          </div>
        </div>

        <div v-if="allChartList.length > 0" class="section">
          <div class="fieldset-title">
            <span>SELECT data points</span>
          </div>
          <div class="advance-reporting-select-all-container">
            <div class="advance-reporting-checkbox-container">
              <input
                type="checkbox"
                :id="'select-all'"
                :checked="areAllChartsSelected"
                @change="toggleSelectAll"
                class="checkbox-input"
              />
              <div
                class="checkbox-visual"
                :class="{ checked: areAllChartsSelected }"
                role="button"
                @click="toggleSelectAll"
              >
                <i class="ri-check-line" v-if="areAllChartsSelected"></i>
              </div>
            </div>
            <label :for="'select-all'" class="advance-reporting-checkbox-label"
              >Select All</label
            >
          </div>
          <div class="advance-reporting-data-point-grid">
            <ChartDataPointComponent
              v-for="chart in allChartList"
              :key="chart.id"
              :chart-id="chart.id"
              :chart-name="chart.name"
              :is-selected="state.selectedCharts.includes(chart.id)"
              :checkbox-id="chart.id"
              @toggle="(chartId, checked) => toggleChart(chartId, checked)"
              @click="
                (chartId) =>
                  toggleChart(chartId, !state.selectedCharts.includes(chartId))
              "
            />
          </div>
        </div>
      </div>

      <ErrorHandler
        v-if="state.savingError != null || state.deletingError != null"
        class="my-2"
        :error="state.savingError || state.deletingError"
        @done="
          () => {
            if (state.savingError) {
              state.savingError = null
            }
            if (state.deletingError) {
              state.deletingError = null
            }
          }
        "
      />

      <div v-if="allChartList.length > 0" class="popup-footer">
        <BaseButton rounded @click="handleCancel">Cancel</BaseButton>
        <LoadingButton
          :is-loading="state.saving"
          rounded
          solid
          @click="handleExport"
          :disabled="!state.selectedFormat || state.selectedCharts.length === 0"
        >
          {{ state.saving ? actionLoadingText : actionText }}
        </LoadingButton>
      </div>
    </template>
  </div>
</template>

<script>
import advanceReportingConstants from "@/constants/advanceReportingConstants"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import ErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import { computed, onMounted, reactive } from "vue"
import { useToast } from "vue-toastification"
import { useStore } from "vuex"
import ChartDataPointComponent from "./ChartDataPointComponent.vue"

export default {
  name: "ExportReportPopUp",
  props: {
    isEmailMode: {
      type: Boolean,
      default: false
    }
  },
  components: {
    BaseButton,
    LoadingButton,
    ChartDataPointComponent,
    ErrorHandler
  },
  emits: ["export", "cancel"],
  setup(props, { emit }) {
    const store = useStore()
    const toast = useToast()

    const state = reactive({
      loading: false,
      saving: false,
      error: null,
      savingError: null,
      deletingError: null,
      selectedFormat:
        advanceReportingConstants?.REPORT_FORMAT_OPTIONS?.[0]?.id || "csv",
      selectedCharts: []
    })

    const CHART_ID_TO_DATA_POINT_KEY = {
      [advanceReportingConstants.DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_BREAKDOWN]:
        "school_year_pass_break_down",
      [advanceReportingConstants.DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_TREND]:
        "school_year_pass_trend_chart",
      [advanceReportingConstants.DEFAULT_CHART_IDS.PASS_BREAKDOWN]:
        "pass_breakdown_specific_date_range",
      [advanceReportingConstants.DEFAULT_CHART_IDS.PASS_TREND]:
        "pass_trend_chart_specific_date_range",
      [advanceReportingConstants.DEFAULT_CHART_IDS.TOTAL_PASSES_BY_SCHOOLS]:
        "total_passes_granted_by_schools",
      [advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_TAKEN]:
        "most_passes_taken",
      [advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_GRANTED]:
        "most_passes_granted",
      [advanceReportingConstants.DEFAULT_CHART_IDS.PASS_BREAKDOWN_BY_GRAD_YEAR]:
        "pass_breakdown_by_grad_year",
      [advanceReportingConstants.DEFAULT_CHART_IDS.AVERAGE_PASS_DURATION]:
        "average_pass_duration",
      [advanceReportingConstants.DEFAULT_CHART_IDS.MOST_FREQUENTED_LOCATIONS]:
        "most_frequented_locations",
      [advanceReportingConstants.DEFAULT_CHART_IDS.TARDY_INFRACTIONS]:
        "tardy_infractions",
      [advanceReportingConstants.DEFAULT_CHART_IDS.CONTACT_CONTROL]:
        "contact_control",
      [advanceReportingConstants.DEFAULT_CHART_IDS.VISITORS_TREND]:
        "visitor_trends",
      [advanceReportingConstants.DEFAULT_CHART_IDS.ZERO_GRANTERS]:
        "zero_granters"
    }

    const actionText = computed(() => {
      return props?.isEmailMode ? "Send to Email" : "Download"
    })

    const actionLoadingText = computed(() => {
      return props.isEmailMode ? "Sending..." : "Downloading..."
    })

    const REPORT_FORMATS =
      advanceReportingConstants.REPORT_FORMAT_OPTIONS.filter(
        (option) => option.active
      )

    const allChartList = computed(() => {
      return store?.getters?.["advanceReportingStore/allChartList"] || []
    })

    const areAllChartsSelected = computed(() => {
      return (
        allChartList.value.length > 0 &&
        state.selectedCharts.length === allChartList.value.length
      )
    })

    const toggleChart = (chartId, checked) => {
      const newSelectedCharts = [...(state.selectedCharts || [])]
      if (checked) {
        newSelectedCharts?.push(chartId)
      } else {
        newSelectedCharts?.splice(newSelectedCharts?.indexOf(chartId), 1)
      }
      state.selectedCharts = newSelectedCharts
    }

    const toggleSelectAll = () => {
      if (areAllChartsSelected.value) {
        state.selectedCharts = []
      } else {
        state.selectedCharts = allChartList.value.map((chart) => chart.id)
      }
    }

    const selectedDateRange = computed(() => {
      return store?.getters?.["advanceReportingStore/selectedDateRange"] || null
    })

    const selectedSchools = computed(() => {
      return store?.state?.advanceReportingStore?.selectedSchools || []
    })

    const kpiData = computed(() => {
      return store?.getters?.["advanceReportingStore/kpiData"] || null
    })

    const dateIndependentChartData = computed(() => {
      return (
        store?.getters?.["advanceReportingStore/dateIndependentChartData"] ||
        null
      )
    })

    const dateDependentChartData = computed(() => {
      return (
        store?.getters?.["advanceReportingStore/dateDependentChartData"] || null
      )
    })

    const frontendIdVsBackendIdMap = computed(() => {
      return (
        store?.getters?.["advanceReportingStore/frontendIdVsBackendIdMap"] ||
        null
      )
    })

    const revealedNamesMatrix = computed(() => {
      return store?.getters?.["advanceReportingStore/revealedNamesMatrix"] || {}
    })

    const transformPieChartData = (data) => {
      if (!data || !Array.isArray(data)) {
        return null
      }
      return {
        pass_type: data.map((item) => item?.name || ""),
        number_of_passes: data.map((item) => Number(item?.value || 0))
      }
    }

    const transformLineChartData = (data, includePreNumber = true) => {
      if (!data || !data.categories || !data.current) {
        return null
      }
      const result = {
        date: data.categories || [],
        number_of_passes: data.current || []
      }
      if (includePreNumber) {
        result.pre_number_of_passes = data.previous || []
      }
      return result
    }

    const transformColumnChartData = (chartId, data) => {
      if (!data || !data.categories || !data.values) {
        return null
      }

      const feChartId = Object.keys(frontendIdVsBackendIdMap.value || {}).find(
        (key) => frontendIdVsBackendIdMap.value[key] === chartId
      )

      switch (feChartId) {
        case advanceReportingConstants.DEFAULT_CHART_IDS
          .TOTAL_PASSES_BY_SCHOOLS: {
          const names = data.categories || []
          const values = data.values || []
          return {
            name_of_school: names.map((name, index) =>
              name ? name : `${index + 1}st place`
            ),
            number_of_passes: values
          }
        }
        case advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_TAKEN: {
          const names = data.categories || []
          const values = data.values || []
          return {
            student_rank: names.map((name, index) =>
              name ? name : `${index + 1}st`
            ),
            number_of_passes: values,
            are_names_revealed: revealedNamesMatrix?.value?.[feChartId] || false
          }
        }
        case advanceReportingConstants.DEFAULT_CHART_IDS.MOST_PASSES_GRANTED: {
          const names = data.categories || []
          const values = data.values || []
          return {
            granter_rank: names,
            number_of_passes: values,
            are_names_revealed: revealedNamesMatrix?.value?.[feChartId] || false
          }
        }
        case advanceReportingConstants.DEFAULT_CHART_IDS
          .PASS_BREAKDOWN_BY_GRAD_YEAR: {
          return {
            graduation_year: data.categories || [],
            number_of_passes: data.values || []
          }
        }
        case advanceReportingConstants.DEFAULT_CHART_IDS
          .AVERAGE_PASS_DURATION: {
          return {
            pass_type: data.categories || [],
            average_time_in_min: data.values || []
          }
        }
        case advanceReportingConstants.DEFAULT_CHART_IDS
          .MOST_FREQUENTED_LOCATIONS: {
          const schools = data.schools || []
          const categories = data.categories || []
          const values = data.values || []
          return {
            location: categories,
            school_name: schools,
            number_of_passes: values
          }
        }
        case advanceReportingConstants.DEFAULT_CHART_IDS.CONTACT_CONTROL: {
          const categories = data.categories || []
          const values = data.values || []
          const schools = data.schools || []
          return {
            pair_ranking: categories,
            school_name: schools,
            number_of_contacts_prevented: values,
            are_names_revealed: revealedNamesMatrix?.value?.[feChartId] || false
          }
        }
        default:
          return null
      }
    }

    const downloadFilesFromUrls = async (fileUrls, filePaths) => {
      if (!fileUrls || !Array.isArray(fileUrls) || fileUrls.length === 0) {
        return { success: false, error: "No files to download" }
      }

      const downloadPromises = fileUrls.map(async (url, index) => {
        try {
          const response = await fetch(url, {
            method: "GET",
            headers: {
              Accept: "*/*"
            }
          })

          if (!response.ok) {
            throw new Error(
              `Failed to download file: ${response.status} ${response.statusText}`
            )
          }

          const blob = await response.blob()
          const fileName =
            filePaths?.[index]?.split("/").pop() ||
            url.split("/").pop()?.split("?")[0] ||
            `report_${index + 1}.csv`

          const link = document.createElement("a")
          const objectUrl = URL.createObjectURL(blob)
          link.href = objectUrl
          link.setAttribute("download", fileName)
          link.style.display = "none"
          document.body.appendChild(link)
          link.click()

          // Cleanup after a short delay to ensure download starts
          setTimeout(() => {
            document.body.removeChild(link)
            URL.revokeObjectURL(objectUrl)
          }, 100)

          return { success: true, fileName }
        } catch (error) {
          return {
            success: false,
            fileName: filePaths?.[index] || url,
            error: error?.message || error
          }
        }
      })

      const results = await Promise.allSettled(downloadPromises)
      const downloadResults = results.map((r) =>
        r.status === "fulfilled" ? r.value : { success: false, error: r.reason }
      )
      const successful = downloadResults.filter((r) => r?.success).length
      const failed = downloadResults.length - successful

      return {
        success: successful > 0,
        successful,
        failed,
        total: results.length,
        results: downloadResults
      }
    }

    const buildPayload = () => {
      const dateRange = selectedDateRange.value
      const schools = selectedSchools.value || []
      const selectedChartBackendIds = state.selectedCharts || []

      const date_range = {
        start_date: dateRange?.startDate || null,
        end_date: dateRange?.endDate || null
      }

      let trend_dates = null
      if (
        dateRange?.type !== advanceReportingConstants.DATE_RANGE_TYPES.CUSTOM &&
        dateRange?.previousStartDate &&
        dateRange?.previousEndDate
      ) {
        trend_dates = {
          start_date: dateRange.previousStartDate,
          end_date: dateRange.previousEndDate
        }
      }

      const schoolsArray =
        schools.length > 0 ? schools.map((s) => s?.name || "") : []

      const data_points = {}

      const usersKpiBeId =
        frontendIdVsBackendIdMap.value?.[
          advanceReportingConstants.DEFAULT_CHART_IDS.USERS_KPI
        ] || null

      if (kpiData.value && selectedChartBackendIds.includes(usersKpiBeId)) {
        data_points.users_shared_and_logged_in = {
          Admin: {
            count: Number(kpiData.value.totalAdmins || 0),
            percentage: Number(kpiData.value.adminTrend || 0)
          },
          Teacher: {
            count: Number(kpiData.value.totalTeachers || 0),
            percentage: Number(kpiData.value.teacherTrend || 0)
          },
          Staff: {
            count: Number(kpiData.value.totalStaff || 0),
            percentage: Number(kpiData.value.staffTrend || 0)
          },
          Student: {
            count: Number(kpiData.value.totalStudents || 0),
            percentage: Number(kpiData.value.studentTrend || 0)
          }
        }
      }

      if (dateIndependentChartData.value) {
        const dateIndependentData = dateIndependentChartData.value

        const schoolYearPassBreakdownFeId =
          advanceReportingConstants.DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_BREAKDOWN
        const schoolYearPassBreakdownBeId =
          frontendIdVsBackendIdMap.value?.[schoolYearPassBreakdownFeId]
        if (
          schoolYearPassBreakdownBeId &&
          selectedChartBackendIds.includes(schoolYearPassBreakdownBeId)
        ) {
          const chartData =
            dateIndependentData[schoolYearPassBreakdownFeId] || null
          const transformed = transformPieChartData(chartData)
          if (transformed) {
            data_points.school_year_pass_break_down = transformed
          }
        }

        const schoolYearPassTrendFeId =
          advanceReportingConstants.DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_TREND
        const schoolYearPassTrendBeId =
          frontendIdVsBackendIdMap.value?.[schoolYearPassTrendFeId]
        if (
          schoolYearPassTrendBeId &&
          selectedChartBackendIds.includes(schoolYearPassTrendBeId)
        ) {
          const chartData = dateIndependentData[schoolYearPassTrendFeId] || null
          const isCustomDateType =
            selectedDateRange.value?.type ===
            advanceReportingConstants.DATE_RANGE_TYPES.CUSTOM
          const transformed = transformLineChartData(
            chartData,
            !isCustomDateType
          )
          if (transformed) {
            data_points.school_year_pass_trend_chart = transformed
          }
        }
      }

      if (dateDependentChartData.value) {
        const dateDependentData = dateDependentChartData.value

        selectedChartBackendIds.forEach((backendId) => {
          const feChartId = Object.keys(
            frontendIdVsBackendIdMap.value || {}
          ).find((key) => frontendIdVsBackendIdMap.value[key] === backendId)

          if (!feChartId) return

          const chartData = dateDependentData[feChartId] || null
          if (!chartData) return

          const dataPointKey = CHART_ID_TO_DATA_POINT_KEY[feChartId]
          if (!dataPointKey) return

          let transformed = null

          if (
            feChartId ===
            advanceReportingConstants.DEFAULT_CHART_IDS.PASS_BREAKDOWN
          ) {
            transformed = transformPieChartData(chartData)
          } else if (
            feChartId ===
              advanceReportingConstants.DEFAULT_CHART_IDS.PASS_TREND ||
            feChartId ===
              advanceReportingConstants.DEFAULT_CHART_IDS.TARDY_INFRACTIONS ||
            feChartId ===
              advanceReportingConstants.DEFAULT_CHART_IDS.VISITORS_TREND ||
            feChartId ===
              advanceReportingConstants.DEFAULT_CHART_IDS.ZERO_GRANTERS
          ) {
            const isCustomDateType =
              selectedDateRange.value?.type ===
              advanceReportingConstants.DATE_RANGE_TYPES.CUSTOM
            transformed = transformLineChartData(chartData, !isCustomDateType)
          } else {
            transformed = transformColumnChartData(backendId, chartData)
          }

          if (transformed) {
            data_points[dataPointKey] = transformed
          }
        })
      }

      return {
        date_range,
        ...(trend_dates && { trend_dates }),
        schools: schoolsArray,
        data_points
      }
    }

    const handleExport = async () => {
      if (!state.selectedFormat || state.selectedCharts.length === 0) {
        return
      }

      try {
        state.saving = true
        state.savingError = null

        const payload = buildPayload()

        // Email mode
        if (props.isEmailMode) {
          payload.type = "email"
          const response = await store.dispatch(
            "advanceReportingStore/downloadOrEmailReport",
            payload
          )
          if (response?.status === "success") {
            toast.success(
              response?.message ||
                "Report will be sent to your registered email shortly."
            )
          }
          emit("cancel")
          return
        }

        // Download mode
        payload.type = "download"
        const response = await store.dispatch(
          "advanceReportingStore/downloadOrEmailReport",
          payload
        )

        if (response?.file_url && Array.isArray(response.file_url)) {
          const fileUrls = response.file_url || []
          const filePaths = response.file_path || []

          const downloadResult = await downloadFilesFromUrls(
            fileUrls,
            filePaths
          )

          if (downloadResult.success) {
            if (downloadResult.failed > 0) {
              toast.warning(
                `Downloaded ${downloadResult.successful} of ${downloadResult.total} files. Some files failed to download.`
              )
            } else {
              toast.success(
                `Successfully downloaded ${downloadResult.successful} file${
                  downloadResult.successful > 1 ? "s" : ""
                }.`
              )
            }
          } else {
            toast.error("Failed to download files. Please try again.")
          }

          if (filePaths.length > 0) {
            try {
              await store.dispatch(
                "advanceReportingStore/deleteReportFiles",
                filePaths
              )
            } catch (deleteError) {
              state.deletingError = deleteError
            }
          }
        }

        emit("cancel")
      } catch (error) {
        state.savingError = error
        toast.error(
          props.isEmailMode
            ? "Shucks! Something went wrong while sending the report to email."
            : "Shucks! Something went wrong while downloading the report."
        )
      } finally {
        state.saving = false
      }
    }

    const handleCancel = () => {
      emit("cancel")
    }

    const getChartSettings = async () => {
      try {
        state.loading = true
        await store.dispatch("advanceReportingStore/fetchChartSettings")
      } catch (error) {
        state.error = error
      } finally {
        state.loading = false
      }
    }

    onMounted(async () => {
      await getChartSettings()
    })

    return {
      state,
      actionText,
      actionLoadingText,
      REPORT_FORMATS,
      allChartList,
      areAllChartsSelected,
      toggleChart,
      toggleSelectAll,
      handleExport,
      handleCancel
    }
  }
}
</script>
